import { AxiosError } from 'axios';
import classnames from 'classnames';
import {
  PROPOSAL_AFIRMATIVE,
  PROPOSAL_NEGATIVE,
} from 'components/proposal_types';
import MainWrapper from 'MainWrapper';
import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import useQueryParams from 'v2/hooks/useQueryParams';
import BackgroundImages, {
  BG_IMAGES,
} from 'v2/common/Background/BackgroundImages';
import Layout from 'v2/common/Layout/Layout';
import { ProposalEntity } from 'v2/models';
import restFactory, { ApiError, buildApiError } from 'v2/utils/restFactory';
import '../../styles/main.scss';
import ProposalResponse from './ProposalResponse';
import styles from './proposalResponseToken.module.scss';
import StatusContainer, { ProposalStatus } from './StatusContainer';

interface Params {
  tokenId: string;
  userId: string;
}

export type ProposalResponseAccept = {
  action: typeof PROPOSAL_AFIRMATIVE;
  response: string;
  answers: Array<{ question: number; response: string }>;
  prompt_history_id?: number;
};

type ProposalResponseReject = {
  action: typeof PROPOSAL_NEGATIVE;
  response?: never;
  answers?: never;
};

type ProposalResponseType = ProposalResponseAccept | ProposalResponseReject;

const ProposalResponseToken = () => {
  const queryParams = useQueryParams();
  const [busy, setBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [proposal, setProposal] = useState<ProposalEntity>();
  const [proposalStatus, setProposalStatus] = useState<ProposalStatus>();
  const { params } = useRouteMatch<Params>();
  const tokenId = params.tokenId;
  const userId = params.userId;
  const responseByParam = queryParams.get('response') || '';

  const loadProposal = async () => {
    try {
      const proposal = await restFactory.get<ProposalEntity>(
        `/proposals/token/${tokenId}?user_id=${userId}`
      );
      setProposal(proposal);
      setProposalStatus(getProposalStatus(proposal));
    } catch (error) {
      toast.error(buildApiError(error as AxiosError<ApiError>));
    }
  };

  const submit = async (data: ProposalResponseType) => {
    try {
      setBusy(true);
      await restFactory.put(
        `/proposals/token/${tokenId}?user_id=${userId}`,
        data
      );
      setProposalStatus(
        data.action === PROPOSAL_AFIRMATIVE
          ? 'provider-response-accepted'
          : 'provider-response-rejected'
      );
    } catch (error) {
      toast.error(buildApiError(error as AxiosError<ApiError>));
    } finally {
      setIsLoading(false);
      setBusy(false);
    }
  };

  const onReject = async () => {
    await submit({
      action: PROPOSAL_NEGATIVE,
    });
  };

  const onAccept = async (data: ProposalResponseAccept) => {
    await submit(data);
  };

  useEffect(() => {
    loadProposal();
  }, []);

  useEffect(() => {
    if (proposal) {
      if (!proposalStatus && responseByParam === 'reject') {
        onReject();
      } else {
        setIsLoading(false);
      }
    }
  }, [proposal]);

  if (!proposal) {
    return null;
  }

  return (
    <MainWrapper isReady={true}>
      <Layout>
        <div className="pt-5 d-flex justify-content-center">
          <BackgroundImages
            showTopImage={false}
            bottomImage={BG_IMAGES.bottomRightYellowBgImage}
          />
          {!isLoading && (
            <Card
              className={classnames('fix-bg-images shadow', styles.card, {
                [styles.messageCard]:
                  proposalStatus &&
                  proposalStatus !== 'buyer-response-accepted',
                [styles.chatCard]: proposalStatus === 'buyer-response-accepted',
              })}
            >
              {!proposalStatus && (
                <ProposalResponse
                  proposal={proposal}
                  onReject={onReject}
                  onAccept={onAccept}
                  busy={busy}
                />
              )}

              <StatusContainer
                proposalStatus={proposalStatus}
                proposal={proposal}
                isLoading={isLoading}
              />
            </Card>
          )}
        </div>
      </Layout>
    </MainWrapper>
  );
};

export default ProposalResponseToken;

function getProposalStatus(proposal: ProposalEntity): ProposalStatus {
  if (proposal.data_buyer_response?.action === 'negative') {
    return 'buyer-response-rejected';
  }
  if (proposal.data_buyer_response?.action === 'affirmative') {
    return 'buyer-response-accepted';
  }
  if (proposal.data_provider_response?.action === 'affirmative') {
    return 'provider-response-accepted';
  }
  if (proposal.data_provider_response?.action === 'negative') {
    return 'provider-response-rejected';
  }
  return '';
}
